import React from 'react'
import styled from 'styled-components'
import { Sunburst, DualAxes } from '@ant-design/plots'
import { useStats } from '../hooks/useStats'
import { CountStat } from '../composants/stats/CountStat'
import Loader from '../composants/common/Loader'
import Tabs from '@codegouvfr/react-dsfr/Tabs'
import { useMainElPortal } from '../hooks/useMainElPortal'

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;

  section {
    margin-top: 1rem;

    > .wrapper {
      gap: 1rem;
      justify-content: space-around;
      display: flex;
      flex-wrap: wrap;
    }
  }
`

export function StatsPage() {
  const { signalementStats, alertStats, isLoading, enabledCommuneCount } = useStats()
  const { mainElPortal } = useMainElPortal()

  return mainElPortal
    ? mainElPortal(
        <StyledWrapper>
          <h1>Tableau de bord statistiques</h1>
          {isLoading ? (
            <Loader />
          ) : signalementStats && alertStats ? (
            <>
              <section>
                <div className='wrapper'>
                  <CountStat label='Communes actives' count={enabledCommuneCount} />
                </div>
              </section>
              <Tabs
                tabs={[
                  {
                    label: 'Signalements',
                    isDefault: true,
                    content: signalementStats ? (
                      <>
                        <h2>Signalements</h2>
                        <section>
                          <div className='wrapper'>
                            <CountStat label='Total' count={signalementStats.total} />
                            <CountStat label='En attente' count={signalementStats.totalPending} />
                            <CountStat label='Traités' count={signalementStats.totalProcessed} />
                            <CountStat label='Ignorés' count={signalementStats.totalIgnored} />
                            <CountStat label='Expirés' count={signalementStats.totalExpired} />
                          </div>
                        </section>

                        <section>
                          <DualAxes
                            xField='date'
                            animate={{
                              enter: { type: 'waveIn' },
                            }}
                            title={{
                              title: 'Créations et traitements par mois',
                              titleFontSize: 22,
                            }}
                            axis={{
                              x: {
                                labelFormatter: (text: string) => {
                                  const [year, month] = text.split('-')
                                  return `${month}/${year}`
                                },
                              },
                              y: {
                                labelFormatter: (text: string) => `${text}`,
                              },
                            }}
                            tooltip={{
                              title: (datum: any) => {
                                const [year, month] = datum.date.split('-')
                                return `${month}/${year}`
                              },
                              items: [
                                {
                                  channel: 'y',
                                  valueFormatter: (value: number) => value.toLocaleString('fr-FR'),
                                },
                              ],
                            }}
                            {...{
                              children: [
                                {
                                  type: 'interval',
                                  data: signalementStats.byMonth,
                                  yField: 'count',
                                  colorField: 'type',
                                  group: { padding: 0 },
                                  tooltip: {
                                    items: [
                                      (datum: any) => {
                                        const [year, month] = datum.date.split('-')
                                        return {
                                          name: `${datum.type} (${month}/${year})`,
                                          value: datum.count,
                                        }
                                      },
                                    ],
                                  },
                                },
                                {
                                  type: 'line',
                                  data: signalementStats.byMonthStacked,
                                  yField: 'count',
                                  colorField: 'type',
                                  tooltip: {
                                    items: [
                                      (datum: any) => ({
                                        name: `${datum.type} (cumulé)`,
                                        value: datum.count,
                                      }),
                                    ],
                                  },
                                  axis: {
                                    y: {
                                      position: 'right',
                                    },
                                  },
                                },
                              ],
                            }}
                          />
                        </section>

                        <section>
                          <Sunburst
                            data={signalementStats.sourceChartData}
                            animate={{
                              enter: { type: 'waveIn' },
                            }}
                            innerRadius={0.2}
                            title={{
                              title: 'Créations par sources',
                              titleFontSize: 22,
                            }}
                          />
                          <Sunburst
                            data={signalementStats.clientChartData}
                            animate={{
                              enter: { type: 'waveIn' },
                            }}
                            innerRadius={0.2}
                            title={{
                              title: 'Traitements par clients',
                              titleFontSize: 22,
                            }}
                          />
                        </section>
                      </>
                    ) : null,
                  },
                  {
                    label: 'Alertes',
                    content: alertStats ? (
                      <>
                        <h2>Alertes</h2>
                        <section>
                          <div className='wrapper'>
                            <CountStat label='Total' count={alertStats.total} />
                            <CountStat label='En attente' count={alertStats.totalPending} />
                            <CountStat label='Traités' count={alertStats.totalProcessed} />
                            <CountStat label='Ignorés' count={alertStats.totalIgnored} />
                          </div>
                        </section>
                        <section>
                          <DualAxes
                            xField='date'
                            animate={{
                              enter: { type: 'waveIn' },
                            }}
                            title={{
                              title: 'Créations et traitements par mois',
                              titleFontSize: 22,
                            }}
                            axis={{
                              x: {
                                labelFormatter: (text: string) => {
                                  const [year, month] = text.split('-')
                                  return `${month}/${year}`
                                },
                              },
                              y: {
                                labelFormatter: (text: string) => `${text}`,
                              },
                            }}
                            {...{
                              children: [
                                {
                                  type: 'interval',
                                  data: alertStats.byMonth,
                                  yField: 'count',
                                  colorField: 'type',
                                  group: { padding: 0 },
                                  tooltip: {
                                    items: [
                                      (datum: any) => {
                                        const [year, month] = datum.date.split('-')
                                        return {
                                          name: `${datum.type} (${month}/${year})`,
                                          value: datum.count,
                                        }
                                      },
                                    ],
                                  },
                                },
                                {
                                  type: 'line',
                                  data: alertStats.byMonthStacked,
                                  yField: 'count',
                                  colorField: 'type',
                                  tooltip: {
                                    items: [
                                      (datum: any) => ({
                                        name: `${datum.type} (cumulé)`,
                                        value: datum.count,
                                      }),
                                    ],
                                  },
                                },
                              ],
                            }}
                          />
                        </section>
                        <section>
                          <Sunburst
                            data={alertStats.sourceChartData}
                            animate={{
                              enter: { type: 'waveIn' },
                            }}
                            innerRadius={0.2}
                            title={{
                              title: 'Créations par sources',
                              titleFontSize: 22,
                            }}
                          />
                          <Sunburst
                            data={alertStats.clientChartData}
                            animate={{
                              enter: { type: 'waveIn' },
                            }}
                            innerRadius={0.2}
                            title={{
                              title: 'Traitements par clients',
                              titleFontSize: 22,
                            }}
                          />
                        </section>
                      </>
                    ) : null,
                  },
                ]}
              />
            </>
          ) : null}
        </StyledWrapper>,
      )
    : null
}
